import {
  integrationHPDBPayloadSchema,
  IntegrationHPDBPayloadSchema,
  useIntegrationHPDBCreate,
  useIntegrationHPDBs,
} from "@/api/generate-hpdb";
import { useDemographics, useServicables } from "@/api/master-param";
import { FieldCombobox } from "@/components/form/field-combobox";
import { FormContainer } from "@/components/form/form-container";
import { SubmitButton } from "@/components/ui/button";
import { MISSING_ID_IN_ROUTE } from "@/utils/constant";
import { swalError, swalSuccess } from "@/utils/lib/swal";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

export function DownloadHPDBForm({
  isReadOnly = false,
  onFormDataChange,
}: {
  isReadOnly: boolean;
  onFormDataChange: (hasData: boolean) => void;
}) {
  const [demographicSearch, setDemographicSearch] = useState("");
  const [servicableSearch, setServicableSearch] = useState("");

  const { id } = useParams();
  if (!id) {
    throw new Error(MISSING_ID_IN_ROUTE);
  }

  const { data: integrationHPDBs } = useIntegrationHPDBs({ rfsId: id });
  const { data: demographics } = useDemographics({
    search: demographicSearch,
  });
  const { data: servicables } = useServicables({
    search: servicableSearch,
  });
  const integrationHPDB = integrationHPDBs?.[0];

  useEffect(() => {
    const hasData = !!integrationHPDB;
    onFormDataChange(hasData);
  }, [integrationHPDB, onFormDataChange]);

  const form = useForm<IntegrationHPDBPayloadSchema>({
    resolver: zodResolver(integrationHPDBPayloadSchema),
    defaultValues: {
      rfs: {
        id: id,
      },
      demographic: integrationHPDB?.demographic,
      serviceable: integrationHPDB?.serviceable,
      homepassId: integrationHPDB?.homepassId,
      ownership: integrationHPDB?.ownership,
      partnerRfsDate: integrationHPDB?.partnerRfsDate,
      status: integrationHPDB?.status,
      vendorName: integrationHPDB?.vendorName,
    },
    values: {
      rfs: {
        id: id,
      },
      demographic: integrationHPDB?.demographic ?? "",
      serviceable: integrationHPDB?.serviceable ?? "",
      homepassId: integrationHPDB?.homepassId ?? "",
      ownership: integrationHPDB?.ownership ?? "",
      partnerRfsDate: integrationHPDB?.partnerRfsDate ?? "",
      status: integrationHPDB?.status ?? "UNPROCESSED",
      vendorName: integrationHPDB?.vendorName ?? "",
    },
  });

  const { mutate, isPending } = useIntegrationHPDBCreate();

  const handleSubmit = (values: IntegrationHPDBPayloadSchema) => {
    mutate(
      {
        data: {
          rfs: {
            id: id,
          },
          demographic: values.demographic,
          serviceable: values.serviceable,
          homepassId: values.homepassId,
          ownership: values.ownership,
          partnerRfsDate: values.partnerRfsDate,
          status: values.status,
          vendorName: values.vendorName,
        },
      },
      {
        onSuccess: () => {
          swalSuccess.fire({
            text: "Successfully download HPDB",
          });
        },
        onError: (error) => {
          swalError.fire({
            text: error.message,
          });
        },
      },
    );
  };

  return (
    <FormContainer form={form} onSubmit={handleSubmit}>
      <div className="space-y-4 md:columns-2">
        <FieldCombobox
          name="demographic"
          label="Demographic"
          search={demographicSearch}
          setSearch={setDemographicSearch}
          hasRequiredAsterisk
          isDisabled={isReadOnly || Boolean(integrationHPDB)}
          options={
            demographics?.content?.map((demographic) => ({
              value: demographic.value,
              label: demographic.value,
            })) ?? []
          }
        />
        <FieldCombobox
          name="serviceable"
          label="Serviceable"
          search={servicableSearch}
          setSearch={setServicableSearch}
          hasRequiredAsterisk
          isDisabled={isReadOnly || Boolean(integrationHPDB)}
          options={
            servicables?.content?.map((servicable) => ({
              value: servicable.value,
              label: servicable.value,
            })) ?? []
          }
        />
      </div>
      <SubmitButton
        loading={isPending}
        disabled={isReadOnly || Boolean(integrationHPDB)}
        className="ml-auto"
      />
    </FormContainer>
  );
}
